import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Send, X, FileText, Image as ImageIcon, MessageSquareText, Settings2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtDateTime } from "@/lib/api";

const openFile = async (m) => {
  const win = window.open("", "_blank");
  try {
    const { data } = await api.get(`/messages/${m.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    if (win) win.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (win) win.close();
    toast.error(errMsg(err));
  }
};

const Bubble = ({ m, mine, testid }) => {
  const Icon = m.attachment?.content_type === "application/pdf" ? FileText : ImageIcon;
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={testid}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "rounded-br-sm bg-[#3d655b] text-white" : "rounded-bl-sm bg-white text-[#0f172a] shadow-sm"}`}>
        {m.auto && <div className={`mb-1 text-[10px] font-bold uppercase tracking-wider ${mine ? "text-white/70" : "text-[#b8956f]"}`} data-testid={`${testid}-auto`}>Risposta automatica</div>}
        {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
        {m.attachment && (
          <button type="button" onClick={() => openFile(m)} data-testid={`${testid}-file`}
            className={`mt-1 flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${mine ? "bg-white/15" : "bg-[#edf2ef] text-[#2f4f47]"}`}>
            <Icon className="h-4 w-4" /> <span className="max-w-[180px] truncate">{m.attachment.filename}</span>
          </button>
        )}
        <div className={`mt-1 text-[10px] ${mine ? "text-white/70" : "text-slate-400"}`}>{fmtDateTime(m.created_at)}</div>
      </div>
    </div>
  );
};

const QuickEditor = ({ open, onOpenChange, items, onSaved }) => {
  const [v, setV] = useState("");
  useEffect(() => { if (open) setV(items.join("\n")); }, [open, items]);
  const save = async () => {
    try {
      const { data } = await api.put("/settings/quick-replies", { items: v.split("\n") });
      onSaved(data.items);
      toast.success("Risposte rapide salvate");
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="quick-replies-dialog">
        <DialogHeader><DialogTitle>Risposte rapide</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Scrivi una risposta per riga.</p>
        <textarea className="field min-h-[220px]" value={v} onChange={(e) => setV(e.target.value)} data-testid="quick-replies-textarea" />
        <div className="flex justify-end"><button className="btn-primary" onClick={save} data-testid="quick-replies-save">Salva</button></div>
      </DialogContent>
    </Dialog>
  );
};

const QuickReplies = ({ onPick, prefix }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  useEffect(() => { api.get("/settings/quick-replies").then((r) => setItems(r.data.items)); }, []);
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="btn-ghost shrink-0" title="Risposte rapide" data-testid={`${prefix}-quick-button`}><MessageSquareText className="h-4 w-4" /></button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 rounded-2xl p-2" data-testid={`${prefix}-quick-panel`}>
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {items.length ? items.map((q, i) => (
              <button key={i} type="button" onClick={() => { onPick(q); setOpen(false); }} data-testid={`${prefix}-quick-${i}`}
                className="w-full rounded-xl px-3 py-2 text-left text-sm hover:bg-[#edf2ef]">{q}</button>
            )) : <div className="px-3 py-4 text-center text-sm text-slate-400">Nessuna risposta rapida</div>}
          </div>
          <button type="button" onClick={() => { setOpen(false); setEdit(true); }} data-testid={`${prefix}-quick-manage`}
            className="mt-1 flex w-full items-center gap-2 border-t border-[#e2e8f0] px-3 pt-2 text-xs font-semibold text-[#3d655b]"><Settings2 className="h-3.5 w-3.5" /> Gestisci risposte rapide</button>
        </PopoverContent>
      </Popover>
      <QuickEditor open={edit} onOpenChange={setEdit} items={items} onSaved={setItems} />
    </>
  );
};

const Composer = ({ onSend, prefix, quick }) => {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !file) return;
    setBusy(true);
    const ok = await onSend(text, file);
    setBusy(false);
    if (ok) { setText(""); setFile(null); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <form onSubmit={submit} className="space-y-2 border-t border-[#e2e8f0] p-3">
      {file && (
        <div className="flex items-center justify-between rounded-xl bg-[#edf2ef] px-3 py-2 text-xs" data-testid={`${prefix}-file-preview`}>
          <span className="truncate">{file.name}</span>
          <button type="button" onClick={() => { setFile(null); fileRef.current.value = ""; }} data-testid={`${prefix}-file-remove`}><X className="h-4 w-4" /></button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`${prefix}-file-input`} />
        <button type="button" className="btn-ghost shrink-0" onClick={() => fileRef.current?.click()} data-testid={`${prefix}-attach-button`}><Paperclip className="h-4 w-4" /></button>
        {quick && <QuickReplies prefix={prefix} onPick={(q) => setText((t) => (t.trim() ? `${t} ${q}` : q))} />}
        <textarea rows={2} className="field min-h-[44px] flex-1 resize-none" placeholder="Scrivi un messaggio…" value={text}
          onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }} data-testid={`${prefix}-text-input`} />
        <button className="btn-primary shrink-0" disabled={busy || (!text.trim() && !file)} data-testid={`${prefix}-send-button`}><Send className="h-4 w-4" /></button>
      </div>
    </form>
  );
};

export function ChatBox({ url, me, prefix = "chat", onChange, height = "h-[420px]" }) {
  const [items, setItems] = useState([]);
  const endRef = useRef(null);
  const load = useCallback(() => api.get(url).then((r) => { setItems(r.data); onChange?.(); }), [url, onChange]);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [items.length]);
  const send = async (text, file) => {
    const fd = new FormData();
    fd.append("text", text);
    if (file) fd.append("file", file);
    try {
      await api.post(url, fd);
      await load();
      return true;
    } catch (err) {
      toast.error(errMsg(err));
      return false;
    }
  };
  return (
    <div className="overflow-hidden rounded-2xl border border-[#e2e8f0] bg-[#f8fafc]" data-testid={`${prefix}-box`}>
      <div className={`${height} space-y-3 overflow-y-auto p-4`} data-testid={`${prefix}-messages`}>
        {items.length ? items.map((m) => <Bubble key={m.id} m={m} mine={m.sender === me} testid={`${prefix}-msg-${m.id}`} />)
          : <div className="grid h-full place-items-center text-center text-sm text-slate-400" data-testid={`${prefix}-empty`}>Nessun messaggio. Scrivi qui le tue domande.</div>}
        <div ref={endRef} />
      </div>
      <Composer onSend={send} prefix={prefix} quick={me === "admin"} />
    </div>
  );
}
